import { useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUserData, useSignOut } from '@nhost/react';
import { gql, useQuery } from '@apollo/client';
import { useTenant } from './TenantContext';
import { TenantPicker } from './TenantPicker';
import { NotificationBell } from './NotificationBell';

type NavGroup = {
  label: string;
  items: ReadonlyArray<{
    to: string;
    icon: string;
    label: string;
    badgeKey?: 'pending';
  }>;
};

const NAV: readonly NavGroup[] = [
  {
    label: 'Work',
    items: [
      { to: '/', icon: 'space_dashboard', label: 'Overview' },
      { to: '/reviews', icon: 'forum', label: 'Reviews', badgeKey: 'pending' },
      { to: '/insights', icon: 'analytics', label: 'Insights' },
      { to: '/carlos', icon: 'auto_awesome', label: 'Carlos (AI Agent)' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/products', icon: 'inventory_2', label: 'Products' },
      { to: '/moderation-rules', icon: 'rule', label: 'Moderation' },
    ],
  },
  {
    label: 'Configure',
    items: [
      { to: '/integrations', icon: 'extension', label: 'Integrations' },
      { to: '/auto-reports', icon: 'schedule_send', label: 'Auto-Reports' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/team', icon: 'group', label: 'Team' },
      { to: '/settings', icon: 'settings', label: 'Settings' },
    ],
  },
];

const PENDING_QUERY = gql`
  query PendingCount {
    pending: reviews_aggregate(where: { status: { _eq: pending } }) { aggregate { count } }
  }
`;

const COLLAPSED_KEY = 'tomi.sidebar.collapsed';

export function Shell({ children }: { children: React.ReactNode }) {
  const { selected, setSelected, collapsed, setCollapsed } = useTenant();
  const user = useUserData();
  const { signOut } = useSignOut();
  const location = useLocation();

  const { data: pendingData } = useQuery(PENDING_QUERY, { fetchPolicy: 'cache-and-network' });
  const pendingCount: number = pendingData?.pending?.aggregate?.count ?? 0;

  useEffect(() => {
    try {
      const v = localStorage.getItem(COLLAPSED_KEY);
      if (v === '1') setCollapsed(true);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleCollapsed() {
    setCollapsed(!collapsed);
    try {
      localStorage.setItem(COLLAPSED_KEY, !collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  const initials = useMemo(() => {
    const name = user?.displayName || user?.email || 'User';
    return name
      .split(/[\s.@]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('');
  }, [user]);

  const pageTitle = useMemo(() => {
    const path = location.pathname;
    for (const g of NAV) {
      for (const it of g.items) {
        if (it.to === path || (it.to !== '/' && path.startsWith(it.to))) return it.label;
      }
    }
    return 'Overview';
  }, [location.pathname]);

  return (
    <div className="app">
      <aside className={`rail${collapsed ? ' collapsed' : ''}`} aria-label="Hauptnavigation">
        <div className="brand">
          <img src="/tomi-icon.png" alt="" />
          {!collapsed && <span className="brand-name">Tomi Reviews</span>}
          <button
            type="button"
            className="rail-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Sidebar einblenden' : 'Sidebar einklappen'}
            title={collapsed ? 'Sidebar einblenden' : 'Sidebar einklappen'}
          >
            <span className="material-symbols-rounded">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>

        <TenantPicker selected={selected} onChange={setSelected} collapsed={collapsed} />

        <nav>
          {NAV.map((group, gi) => (
            <div key={group.label}>
              {!collapsed && <div className="nav-section">{group.label}</div>}
              {collapsed && gi > 0 && <div className="nav-divider" />}
              {group.items.map((it) => {
                const showBadge = it.badgeKey === 'pending' && pendingCount > 0;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === '/'}
                    className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                    title={collapsed ? it.label : undefined}
                  >
                    <span className="material-symbols-rounded">{it.icon}</span>
                    {!collapsed && <span>{it.label}</span>}
                    {showBadge && !collapsed && (
                      <span className="nav-count">{pendingCount > 99 ? '99+' : pendingCount}</span>
                    )}
                    {showBadge && collapsed && <span className="nav-dot" aria-hidden="true" />}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <main className="main">
        <header className="topbar">
          <h1>{pageTitle}</h1>
          <div className="crumb">
            <span className="material-symbols-rounded">chevron_right</span>
            <span>Tomi Reviews</span>
            <span className="material-symbols-rounded">chevron_right</span>
            <span>{pageTitle}</span>
          </div>
          <div className="topbar-right">
            <button type="button" className="iconbtn" aria-label="Suche">
              <span className="material-symbols-rounded">search</span>
            </button>
            <NotificationBell />
            <button
              type="button"
              className="user-avatar"
              title={user?.email ?? 'User'}
              onClick={() => signOut()}
              aria-label="Abmelden"
            >
              {initials}
            </button>
          </div>
        </header>
        {children}
      </main>
    </div>
  );
}

export { TenantContext } from './TenantContext';
